import type { ComponentType, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { CMP_BASE_URL } from '@frontend/envConfig';

type CmpEmbedProviderProps = {
  baseUrl: string;
  children: ReactNode;
  onTokenExpired?: () => void;
  orgSsoId: string;
  token: string;
};

type EmbeddableTaskProps = {
  className?: string;
  defaultTab?: 'content' | 'history';
  onApprove?: () => void;
  onReject?: (reason: string) => void;
  taskId: string;
};

type CmpModules = {
  CmpEmbedProvider: ComponentType<CmpEmbedProviderProps>;
  EmbeddableTask: ComponentType<EmbeddableTaskProps>;
};

let cached: CmpModules | null = null;
let inflight: Promise<CmpModules> | null = null;
let polyfilled = false;

/**
 * Pull the component out of a federated module.
 *
 * A container whose exposed module omits `__esModule` gets wrapped again by
 * webpack's interop, so the component ends up at `default.default` rather than
 * `default`. Mirrors the same unwrap in `useDamModules`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrapComponent<P>(mod: any): ComponentType<P> | null {
  return (
    (typeof mod?.default === 'function' && mod.default) ||
    (typeof mod?.default?.default === 'function' && mod.default.default) ||
    null
  );
}

async function loadModules(): Promise<CmpModules> {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    // Phase 1: load the globals the CMP remotes expect, and install them on
    // `window` BEFORE importing the remotes. The MFECmp/* modules reference
    // ambient `_` / `moment` / `window.features` at module-evaluation time, so
    // they must be polyfilled first — otherwise evaluating the remote throws
    // `ReferenceError: _ is not defined`.
    const [moment, _] = await Promise.all([import('moment'), import('lodash-es')]);

    if (!polyfilled) {
      window.moment = moment.default ?? moment;
      // @ts-expect-error -- stub lodash for CMP
      window._ = _;
      // @ts-expect-error -- stub window.features for CMP (feature-flag gate it reads at module load)
      window.features ??= 'axiom,rebrand';
      // @ts-expect-error -- stub the CMP embed base URL so CMP's socket connects
      // to the CMP origin (ws.<cmp-host>) instead of deriving it from the host
      // app's window.origin. Must be set before the remote is imported because
      // the socket reads it at module-evaluation time. See cmp-client#12055.
      window.CMP_EMBED_BASE_URL ??= CMP_BASE_URL;
      // @ts-expect-error -- stub window.analytics for CMP
      window.analytics ??= {
        group: () => {},
        identify: () => {},
        load: () => {},
        page: () => {},
        track: () => {},
      };
      polyfilled = true;
    }

    // Phase 2: now that the globals exist, import the CMP remotes.
    const [cmpEmbedProviderModule, embeddableTaskModule] = await Promise.all([
      import('MFECmp/CmpEmbedProvider'),
      import('MFECmp/EmbeddableTask'),
    ]);

    const CmpEmbedProvider = unwrapComponent<CmpEmbedProviderProps>(cmpEmbedProviderModule);
    const EmbeddableTask = unwrapComponent<EmbeddableTaskProps>(embeddableTaskModule);

    // A remote can resolve to something that is not a component at all.
    // Rendering that throws "Element type is invalid ... but got: object" from
    // inside React's reconciler, which escapes this module entirely. Reject
    // here so the caller sees `error` and renders its fallback instead.
    if (!CmpEmbedProvider || !EmbeddableTask) {
      throw new Error('MFECmp resolved to a non-component export');
    }

    cached = { CmpEmbedProvider, EmbeddableTask };
    return cached;
  })().finally(() => {
    // Clear on failure too, so the next open retries instead of replaying the
    // same rejected promise forever.
    inflight = null;
  });

  return inflight;
}

export function useCmpModules(load: boolean) {
  const [modules, setModules] = useState<CmpModules | null>(cached);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!load) return;
    if (cached) {
      setModules(cached);
      return;
    }
    let cancelled = false;
    // Clear the previous failure, otherwise a successful retry still renders
    // the error branch.
    setError(null);
    loadModules()
      .then(m => {
        if (!cancelled) setModules(m);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { ...modules, isLoaded: modules !== null, error };
}
